import { z } from 'zod'

/**
 * 경량 포인트 원장 API 계약 (BIZ-74).
 *
 * 잔액은 원장 합이고, 각 내역 행은 처리 직후 잔액(balanceAfter)과 직전 잔액(balanceBefore)을
 * 함께 담는다. 운영자·기업이 잔액·내역을 조회하고, 운영자가 수동 적립/조정한다.
 * 주문 시 차감(REDEEM)은 결제 축(BIZ-70)에서 붙는다.
 */

// REFUND = 취소 시 사용(REDEEM)한 포인트를 원 lot에 되돌리는 양수 행(만료일 유지).
export const pointType = z.enum(['EARN', 'REDEEM', 'CLAWBACK', 'EXPIRE', 'ADJUST', 'REFUND'])
export type PointType = z.infer<typeof pointType>

export const pointTransactionItem = z.object({
  id: z.number().int(),
  type: pointType,
  amount: z.number().int(), // 부호 포함(적립 +, 차감 −)
  balanceBefore: z.number().int(),
  balanceAfter: z.number().int(),
  orderId: z.number().int().nullable(),
  invoiceId: z.number().int().nullable(),
  memo: z.string().nullable(),
  createdByName: z.string().nullable(), // 배치·시스템 적립은 null
  expiresAt: z.string().nullable(),
  availableAt: z.string().nullable(), // 이 적립분이 사용가능해지는 날(성숙). 적립행(lot)에만.
  lotRemaining: z.number().int().nullable(), // 이 적립분의 남은 잔량(적립행에만).
  createdAt: z.string(),
})
export type PointTransactionItem = z.infer<typeof pointTransactionItem>

/** 성숙(익월)·만료(2년)를 반영한 잔액 분해(BIZ-74 재설계). 모두 정수(P). */
export const pointBalances = z.object({
  balance: z.number().int(), // 총 보유 = Σ원장금액 (= Σ lot 잔량)
  available: z.number().int(), // 지금 사용 가능(성숙됐고 안 만료)
  pending: z.number().int(), // 아직 성숙 전(익월 지급 예정)
  expiringSoon: z.number().int(), // 30일 내 만료 예정(사용가능 중)
  expiredTotal: z.number().int(), // 만료로 소멸된 누계
  earnedTotal: z.number().int(), // 적립 누계(EARN 합)
})
export type PointBalances = z.infer<typeof pointBalances>

/** 잔액 + 최근 내역. balance는 총 보유(하위호환), balances는 성숙·만료 반영 분해. */
export const pointLedgerResponse = z.object({
  balance: z.number().int(),
  balances: pointBalances,
  transactions: z.array(pointTransactionItem),
})
export type PointLedgerResponse = z.infer<typeof pointLedgerResponse>

/**
 * 주문 화면용 경량 포인트 요약(BIZ-74). 잔액 + 회사 적립율(0~1). 기업 담당자·관리자 모두 접근 가능
 * (내 회사 상세는 관리자 전용이라 담당자가 결제 UI에서 잔액을 못 보는 문제를 피한다).
 */
export const pointSummaryResponse = z.object({
  balance: z.number().int(), // 총 보유(하위호환)
  available: z.number().int(), // 지금 사용 가능(주문 결제에 쓸 수 있는 값)
  pending: z.number().int(), // 익월 지급 예정
  nextAvailableAt: z.string().nullable(), // 대기분이 처음 성숙하는 날(없으면 null)
  earnRate: z.number(), // 유효 적립율(0~1) — 회사 오버라이드 ?? 전역 기본
  discountRate: z.number(), // 유효 할인율(0~1)
})
export type PointSummaryResponse = z.infer<typeof pointSummaryResponse>

/**
 * 운영자 수동 적립/조정. amount 양수=적립, 음수=회수·조정(0 불가).
 * expiresAt은 적립(양수)에만 의미 있고 음수엔 무시된다.
 */
export const grantPointsRequest = z
  .object({
    amount: z
      .number({ invalid_type_error: '숫자를 입력하세요' })
      .int('정수를 입력하세요')
      .refine((v) => v !== 0, { message: '0이 아닌 값을 입력하세요' }),
    memo: z.string().trim().max(200).optional(),
    expiresAt: z.string().date().optional(),
    // 성숙일(이 날부터 사용가능). 미지정 시 즉시(오늘). 적립(양수)에만 의미. 만료는 이 날+2년.
    availableAt: z.string().date().optional(),
  })
  .strict()
  // 만료일은 성숙일보다 뒤여야 한다(point_lot CHECK와 정합 — 어기면 DB 500 대신 여기서 400).
  .refine((d) => !(d.expiresAt && d.availableAt) || d.expiresAt > d.availableAt, {
    message: '만료일은 성숙일보다 뒤여야 합니다',
    path: ['expiresAt'],
  })
export type GrantPointsRequest = z.infer<typeof grantPointsRequest>

export const PointErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  INSUFFICIENT_POINTS: 'INSUFFICIENT_POINTS', // 차감 후 잔액이 음수가 됨
} as const
export type PointErrorCode = (typeof PointErrorCode)[keyof typeof PointErrorCode]
